/**
 * Detaljplan – 6 underrubriker.
 */
export const detaljplanRules = [
  {
    slug: "vad-ar-en-detaljplan",
    title: "Vad är en detaljplan?",
    h1: "Vad är en detaljplan? – Enkelt förklarat",
    metaDescription:
      "Detaljplanen är kommunens juridiskt bindande plan över hur mark får användas och byggas på. Förklarat med exempel.",
    keywords: "vad är detaljplan, detaljplan förklaring, DP",
    intro:
      "En detaljplan (DP) är kommunens bindande plan över markanvändning och byggrätter inom ett område. Den anger vad, hur mycket och var man får bygga.",
    excerpt: {
      text: "Med en detaljplan får kommunen reglera användningen av mark- och vattenområden.",
      source: "PBL 4 kap. 2 §",
    },
    examples: [
      { type: "Villa", title: "B – bostäder", text: "Beteckningen 'B' betyder att marken får användas för bostäder." },
      { type: "Villa", title: "II – två våningar", text: "Romerska siffror anger tillåtet antal våningar." },
    ],
    mistakes: [
      "Att förväxla detaljplan med översiktsplan – DP är juridiskt bindande, ÖP är vägledande.",
      "Att missa gällande äldre detaljplaner – vissa gäller från 1930-talet med udda beteckningar.",
    ],
    municipality_variations:
      "Varje detaljplan är unik – två grannkvarter kan ha helt olika regler.",
    faq: [
      { q: "Var hittar jag min detaljplan?", a: "Kommunens digitala kartportal eller genom att kontakta bygglovhandläggaren." },
      { q: "Är detaljplanen alltid uppdaterad?", a: "Nej, många är från 1970-80-tal med gamla beteckningar." },
    ],
    related: [
      "detaljplan/dp-prickmark",
      "detaljplan/dp-byggnadsarea",
      "detaljplan/dp-hojdbegransning",
    ],
    updated: "2026-07-04",
  },
  {
    slug: "dp-prickmark",
    title: "Prickmark i detaljplan",
    h1: "Prickmark – mark där byggnad inte får uppföras",
    metaDescription:
      "Prickmark i detaljplan innebär att byggnad inte får placeras där. Så identifierar du och söker avvikelse.",
    keywords: "prickmark, prickad mark, byggförbud detaljplan",
    intro:
      "Prickmark är områden i detaljplanen där byggnad inte får uppföras. Ofta pga skyddsavstånd, kulturmiljö eller vatten.",
    examples: [
      { type: "Villa", title: "Prickad förgård", text: "Många detaljplaner har 4-6 m prickmark mot gata – där får inte huvudbyggnad placeras." },
      { type: "Attefall", title: "Attefall på prickmark", text: "Även attefallshus omfattas av byggförbudet på prickmark." },
    ],
    mistakes: [
      "Att placera attefallshus på prickmark utan att söka avvikelse.",
      "Att förväxla prickmark med kryssmark (som endast tillåter komplementbyggnader).",
    ],
    municipality_variations:
      "Tolkningen kan variera – vissa kommuner ger avvikelse lättare, andra är strikta.",
    faq: [
      { q: "Kan man bygga på prickmark?", a: "Endast via 'liten avvikelse' i bygglov – om åtgärden är förenlig med planens syfte." },
    ],
    related: ["detaljplan/vad-ar-en-detaljplan", "detaljplan/dp-byggnadsarea"],
    updated: "2026-07-04",
  },
  {
    slug: "dp-byggnadsarea",
    title: "Byggnadsarea i detaljplan",
    h1: "Byggnadsarea (BYA) – så mäts den",
    metaDescription:
      "Byggnadsarea (BYA) i detaljplan förklaras: hur den mäts, vad som räknas in och undantag för balkong och burspråk.",
    keywords: "byggnadsarea, BYA, e-tal detaljplan",
    intro:
      "Byggnadsarea (BYA) är den yta byggnaden tar på marken – mätt vid fasadens utsida ovanpå socklarna.",
    measurements: [
      { label: "Definition", value: "Yta byggnad tar upp på marken, mätt på utsida fasad" },
      { label: "Räknas EJ in", value: "Balkonger, burspråk, skärmtak" },
      { label: "Vanligt max i detaljplan", value: "150-250 m² BYA för enfamiljshus" },
    ],
    examples: [
      { type: "Villa", title: "e-tal 0,2", text: "e-tal 0,2 = max 20 % av tomtens yta får bebyggas. 800 m² tomt → 160 m² BYA." },
    ],
    mistakes: [
      "Att räkna bostadsarea (BOA) istället för BYA.",
      "Att glömma att attefallshus räknas till fastighetens totala BYA i vissa planer.",
    ],
    municipality_variations:
      "Nyare planer använder ofta e-tal (%), äldre planer anger absolut area i m².",
    faq: [
      { q: "Räknas altan in i BYA?", a: "Bara om altanen har tak – då räknas den som byggnad. Öppen altan räknas inte." },
      { q: "Skiljer BYA och BTA?", a: "BYA = ytan byggnaden tar upp på marken. BTA = summa av alla våningars area." },
    ],
    related: ["detaljplan/dp-hojdbegransning", "detaljplan/dp-prickmark"],
    updated: "2026-07-04",
  },
  {
    slug: "dp-hojdbegransning",
    title: "Höjdbegränsningar i detaljplan",
    h1: "Höjdbegränsningar – nockhöjd, byggnadshöjd och totalhöjd",
    metaDescription:
      "Nockhöjd, byggnadshöjd, totalhöjd förklarade. Så mäts höjder i detaljplan och vad som räknas in.",
    keywords: "byggnadshöjd, nockhöjd, totalhöjd, höjdbegränsning",
    intro:
      "Detaljplaner reglerar höjd genom byggnadshöjd, nockhöjd och/eller totalhöjd. Definitionerna skiljer sig – här förklaras alla.",
    measurements: [
      { label: "Byggnadshöjd", value: "Från medelmarknivå till skärningen mellan fasad och tak" },
      { label: "Nockhöjd", value: "Från medelmarknivå till taknock" },
      { label: "Totalhöjd", value: "Från medelmarknivå till högsta punkten (skorsten, antenn)" },
    ],
    examples: [
      { type: "Villa", title: "Byggnadshöjd 4,5 m", text: "Innebär att fasaden får vara max 4,5 m – taket kan sedan gå högre om det är lutande." },
    ],
    mistakes: [
      "Att räkna taknock som byggnadshöjd – det är två olika saker.",
      "Att glömma att skorsten och antenn ingår i totalhöjd.",
    ],
    municipality_variations:
      "Vilken höjdtyp planen använder varierar – nyare planer använder oftare nockhöjd + totalhöjd.",
    faq: [
      { q: "Räknas takkupa i höjden?", a: "Ja, om den bryter taket väsentligt. Nej om den är del av taket." },
    ],
    related: ["detaljplan/dp-byggnadsarea", "bygglov/tillbyggnad"],
    updated: "2026-07-04",
  },
];
